/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { useEffect, useRef } from 'react';
import { isWindowInView } from '../utils';
import type { DesktopState } from '../windowSystem';

export const MEMORY_LOG_STORAGE_KEY = 'pa:memory-log';
const MEMORY_LOG_INTERVAL_MS = 30_000;
const MB = 1024 * 1024;

/** Chrome-only heap counters (`performance.memory`); absent in other browsers. */
interface ChromePerformanceMemory {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
}

export interface MemorySample {
  domNodes: number;
  heapLimitMb?: number;
  heapTotalMb?: number;
  heapUsedMb?: number;
  inViewWindows: string[];
  openWindows: string[];
}

declare global {
  interface Window {
    /** Logs a memory sample now (available while memory logging is on). */
    paMemory?: () => MemorySample;
  }
}

const toMb = (bytes: number) => Math.round((bytes / MB) * 10) / 10;

const isDevDefault = () => import.meta.env.DEV && import.meta.env.MODE !== 'test';

export const isMemoryLogEnabled = () => {
  try {
    const flag = window.localStorage.getItem(MEMORY_LOG_STORAGE_KEY);
    return flag === null ? isDevDefault() : flag === '1';
  } catch {
    return isDevDefault();
  }
};

export const sampleMemory = (desktopState: DesktopState): MemorySample => {
  const memory = (performance as Performance & { memory?: ChromePerformanceMemory }).memory;
  const focusedWindow = desktopState.windows.find(
    ({ id, isOpen, isMinimized }) => id === desktopState.focusedWindowId && isOpen && !isMinimized,
  );
  const openWindows = desktopState.windows.filter(({ isOpen }) => isOpen);

  return {
    domNodes: document.getElementsByTagName('*').length,
    heapLimitMb: memory && toMb(memory.jsHeapSizeLimit),
    heapTotalMb: memory && toMb(memory.totalJSHeapSize),
    heapUsedMb: memory && toMb(memory.usedJSHeapSize),
    inViewWindows: openWindows
      .filter((windowState) =>
        isWindowInView({
          focusedWindowId: desktopState.focusedWindowId,
          isFocusedWindowMaximized: Boolean(focusedWindow?.isMaximized),
          windowState,
        }),
      )
      .map(({ id }) => id),
    openWindows: openWindows.map(({ id }) => id),
  };
};

const formatDelta = (current: number | undefined, previous: number | undefined) => {
  if (current === undefined || previous === undefined) {
    return '';
  }
  const delta = Math.round((current - previous) * 10) / 10;
  return ` (${delta >= 0 ? '+' : ''}${delta} MB)`;
};

/**
 * Logs heap, DOM size and window counts to the console every 30s so memory growth can be tied
 * to open windows. On in dev; elsewhere set `localStorage['pa:memory-log'] = '1'` and reload.
 * `window.paMemory()` logs a sample on demand.
 */
export function useMemoryLog(desktopState: DesktopState) {
  const stateRef = useRef(desktopState);
  stateRef.current = desktopState;

  useEffect(() => {
    if (!isMemoryLogEnabled()) {
      return;
    }

    let previous: MemorySample | undefined;
    const log = () => {
      const sample = sampleMemory(stateRef.current);
      const heap =
        sample.heapUsedMb === undefined
          ? 'heap n/a (Chrome only)'
          : `heap ${sample.heapUsedMb}/${sample.heapTotalMb} MB${formatDelta(sample.heapUsedMb, previous?.heapUsedMb)}, limit ${sample.heapLimitMb} MB`;
      // biome-ignore lint/suspicious/noConsole: opt-in diagnostics output
      console.info(
        `[pa:memory] ${heap} · ${sample.domNodes} DOM nodes · windows open ${sample.openWindows.length} [${sample.openWindows.join(', ')}] · in view [${sample.inViewWindows.join(', ')}]`,
      );
      previous = sample;
      return sample;
    };

    log();
    const intervalId = window.setInterval(log, MEMORY_LOG_INTERVAL_MS);
    window.paMemory = log;

    return () => {
      window.clearInterval(intervalId);
      delete window.paMemory;
    };
  }, []);
}
